import { useContext, useMemo, useState } from 'react'

import {
  Avatar,
  Box,
  Button,
  Icon,
  Text,
  ThemeColors,
} from '@fairfashion/design-system'
import { useTheme } from 'styled-components'

import { GlobalDataContext } from '../../contexts/globalData'
import { ICompanySegmentAlias } from '../../sdk/company/company.types'
import { docMask, isCNPJ } from '../../utilities/masks/brasilianDocuments'
import { EditCompanyAddressModal } from '../EditCompanyAdressModal/edit-company-address-modal.comp'
import { EditCompanyContactModal } from '../EditCompanyContactModal/edit-company-contact-modal.comp'
import { EditCompanyModal } from '../EditCompanyModal/edit-company-modal.comp'
import { brazilCenterCoords } from '../GoogleMap/google-map.utils'
import { LeafletMap } from '../LeafletMap/leaflet-map.comp'
import { MapMarkerProps } from '../MapCard/map-card.types'

import * as Styles from './company-card.styles'
import { CompanyCardProps } from './company-card.types'

export const CompanyCard = ({
  data,
  user,
  title,
  onEdit,
  onEditLocation,
  onEditContact,
}: CompanyCardProps) => {
  const [openEditCompanyModal, setOpenEditCompanyModal] = useState(false)
  const [openEditCompanyAddressModal, setOpenEditCompanyAddressModal] =
    useState(false)
  const [openEditCompanyContactModal, setOpenEditCompanyContactModal] =
    useState(false)

  const { companySegmentType } = useContext(GlobalDataContext)

  const theme = useTheme()

  const getInitials = (name?: string) => {
    if (!name) return ''

    const [firstName, lastName] = name.split(' ')
    return `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`
  }

  const segment = useMemo(() => {
    if (!companySegmentType) return {} as ICompanySegmentAlias

    return companySegmentType?.[data?.segment ?? 'fashion_company']
  }, [data, companySegmentType])

  const location: MapMarkerProps | undefined = useMemo(() => {
    if (
      data?.address?.geoCoords?.coords?.lat &&
      data?.address?.geoCoords?.coords?.lng
    ) {
      return {
        lat: data?.address?.geoCoords?.coords?.lat,
        lng: data?.address?.geoCoords?.coords?.lng,
        color: `${segment?.color}`,
      }
    }
    return undefined
  }, [data, segment])

  if (!data) return null

  return (
    <Box
      width="100%"
      direction="column"
      gap={2}
      padding={2}
      borderRadius={theme.border_radius.box}
      boxShadow={theme.box_shadow.card}
      backgroundColor="white"
    >
      <Box
        borderRadius={theme.border_radius.card}
        boxShadow={theme.box_shadow.card}
        direction="column"
        gap={2}
        padding={2}
        width="100%"
      >
        <Box width="100%" justifyContent="space-between" alignItems="center">
          <Box flex={1}>
            {title && (
              <Text size="small" fontWeight={500}>
                {title}
              </Text>
            )}
          </Box>
          {onEdit && (
            <Button
              variant="outlined"
              onClick={() => setOpenEditCompanyModal(true)}
            >
              <Icon name="edit" size={20} color="grey_1" />
              Editar
            </Button>
          )}
        </Box>

        <Box alignItems="center" gap={3} flexWrap="wrap">
          <Avatar size={68}>{getInitials(data?.name)}</Avatar>
          <Box direction="column" gap={2} flex={1}>
            <Text fontWeight={500}>{data?.name}</Text>
            <Box gap={2} alignItems="center">
              <Styles.SegmentTag alignItems="center" gap={1}>
                <Icon
                  name="circle_fill"
                  color={segment?.color as ThemeColors}
                  size={8}
                />
                <Text size="small">{segment?.translate}</Text>
              </Styles.SegmentTag>
              <Text size="small">
                {isCNPJ(data?.taxId) ? docMask(data?.taxId) : data?.taxId}
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        borderRadius={theme.border_radius.card}
        boxShadow={theme.box_shadow.card}
        direction="column"
        gap={2}
        padding={2}
        width="100%"
      >
        <Box width="100%" justifyContent="space-between">
          <Text size="small" fontWeight={500}>
            Localização
          </Text>
          {onEditLocation && (
            <Button
              variant="outlined"
              onClick={() => setOpenEditCompanyAddressModal(true)}
            >
              <Icon name="edit" size={20} color="grey_1" />
              Editar
            </Button>
          )}
        </Box>
        <Box gap={5} flexWrap="wrap" width="100%">
          <Box flex={1} alignItems="center" minWidth="200px">
            <Box marginRight={1}>
              <Icon name="map_pin" size={16} color="grey_1" />
            </Box>
            <Text size="small" color="grey_1">
              {data?.address?.geoCoords?.address}
            </Text>
          </Box>
          <Box
            flex={2}
            borderRadius={theme.border_radius.box}
            minWidth="200px"
            height="110px"
            boxShadow={theme.box_shadow.card}
            overflow="hidden"
          >
            <LeafletMap
              center={{
                lat:
                  data?.address?.geoCoords?.coords?.lat ??
                  brazilCenterCoords?.lat,
                lng:
                  data?.address?.geoCoords?.coords?.lng ??
                  brazilCenterCoords?.lng,
              }}
              locations={location ? [location] : []}
              zoom={14}
            />
          </Box>
        </Box>
      </Box>
      {user && (
        <Box
          borderRadius={theme.border_radius.card}
          boxShadow={theme.box_shadow.card}
          direction="column"
          gap={2}
          padding={2}
          width="100%"
        >
          <Box width="100%" justifyContent="space-between">
            <Text size="small" fontWeight={500}>
              Contatos
            </Text>
            {onEditContact && (
              <Button
                variant="outlined"
                onClick={() => setOpenEditCompanyContactModal(true)}
              >
                <Icon name="edit" size={20} color="grey_1" />
                Editar
              </Button>
            )}
          </Box>
          <Box
            width="100%"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
          >
            <Box>
              <Text size="small">{user.name}</Text>
            </Box>
            <Box gap={1} alignItems="center">
              <Icon size={16} color="grey_1" name="mail" />
              <Text size="small">{user.email}</Text>
            </Box>
          </Box>
        </Box>
      )}
      <EditCompanyModal
        title="Editar dados da Minha Empresa"
        data={data}
        open={openEditCompanyModal}
        onClose={() => setOpenEditCompanyModal(false)}
        onSubmit={onEdit}
      />
      <EditCompanyAddressModal
        data={data.address}
        open={openEditCompanyAddressModal}
        onClose={() => setOpenEditCompanyAddressModal(false)}
        onSubmit={onEditLocation}
      />
      <EditCompanyContactModal
        data={user}
        open={openEditCompanyContactModal}
        onClose={() => setOpenEditCompanyContactModal(false)}
        onSubmit={onEditContact}
      />
    </Box>
  )
}
